/**
 * _axios
 * 使用方式同axios
 * method:POST,参数放置data字段中，Content-Type默认为application/x-www-form-urlencoded
 * method:GET,参数放置params字段中，Content-Type默认为application/json
 * */
import axios from 'axios';
import QS from 'qs';
import { notification } from 'antd';
import { getDataType } from '@/shared/functions';
import api from '@/shared/apiConfig';
import loading from '@/components/Loading';
import { token, loginUrl, islocal } from '@/shared/token';
const CancelToken = axios.CancelToken;
/**
 * 缓存请求
 * */
const cacheRequest = {};
/**
 * 辅助工具函数
 */
const axiosTool = {
    throwError: (responseData) => {
        let msg = '';
        // 拼接登录页 sso
        if (+responseData.code !== 200 || !responseData.success) {
            switch (responseData.code) {
                case '10001':
                    msg = '登录超时,请重新登录';
                    islocal ? console.error('这是你本地，超时也不跳') : window.location.replace(loginUrl);
                    break;
                case 'PARAM_MISS':
                    msg = '参数缺失';
                    break;
                default:
                    msg = responseData.msg;
            }
            notification.error({
                message: msg || responseData.msg || responseData.ErrorMessage || '后端接口返回数据为空',
            });
        }
    },
    throwHttpError: (err) => {
        let msg = '';
        let tip = '';
        if (err.response && err.response.status) {
            tip = err.response.config.url;
            switch (+err.response.status) {
                case 400:
                    msg = `请求错误（${err.response.status}）:`;
                    break;
                case 403:
                    msg = `请求被拒绝（${err.response.status}）:`;
                    break;
                case 404:
                    msg = `请求地址出错（${err.response.status}）:`;
                    break;
                case 408:
                    msg = `服务器超时（${err.response.status}）:`;
                    break;
                case 500:
                    msg = `服务器错误（${err.response.status}）:`;
                    break;
                case 501:
                    msg = `请求方式错误（${err.response.status}）:`;
                    break;
                case 502:
                    msg = `网关错误（${err.response.status}）:`;
                    break;
                case 504:
                    msg = `网关超时（${err.response.status}）:`;
                    break;
                default:
                    msg = '';
            }
        }
        else if (err.code === 'ECONNABORTED' && err.message.indexOf('timeout') !== -1) {
            msg = '网络连接超时，请稍后重试';
        }
        else if (err.message === 'Network Error') {
            msg = '网络链接有误';
            tip = '请检查网络连接';
        }
        notification.error({
            message: msg,
            description: tip || '',
        });
    },
};
/**
 * 默认配置
 * */
const axiosConfig = {
    headers: {
        post: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
    },
    transformRequest: (data) => {
        if (getDataType(data) === 'Object') {
            return QS.stringify(data);
        }
        return data;
    },
};
/**
 * 请求拦截器
 */
const requestInterceptors = {
    success: (config) => {
        if (!config.timeout) {
            config.timeout = 50000;
        }
        // 是否隐藏loading
        !(config && config.hideLoading) && loading.show();
        config.headers['Souche-Security-Token'] = token;
        config.headers['souche-security-token-inc'] = token;
        config['url'] = api[config.url] || config.url;
        const cacheUrl = `${config.baseURL}${config.url}`;
        if (cacheRequest[cacheUrl]) {
            if (config.cancelRepeat) {
                cacheRequest[cacheUrl]('取消啦');
                delete cacheRequest[cacheUrl];
            }
            else {
                delete cacheRequest[cacheUrl];
            }
        }
        config.cancelToken = new CancelToken((cancel) => {
            cacheRequest[cacheUrl] = cancel;
        });
        return Object.assign({}, config);
    },
    error: (error) => Promise.reject(error),
};
/**
 * 响应拦截器
 */
const responseInterceptors = {
    success: (response) => {
        const { config } = response;
        !(config && config.hideLoading) && loading.hide();
        if (config.responseType === 'blob') {
            return response.data;
        }
        const responseData = response.data || {};
        if (`${responseData.code}` !== '200' || !responseData.success) {
            axiosTool.throwError(responseData);
            return Promise.reject(responseData);
        }
        const cacheUrl = `${config.baseURL}${config.url}`;
        if (cacheRequest[cacheUrl]) {
            delete cacheRequest[cacheUrl];
        }
        return responseData.data === 'undefined' ? responseData : responseData.data;
    },
    error: (err) => {
        // 抛错
        const { config } = err;
        if (config) {
            const cacheUrl = `${config.baseURL}${config.url}`;
            if (cacheRequest[cacheUrl]) {
                delete cacheRequest[cacheUrl];
            }
            !(config && config.hideLoading) && loading.hide();
            !config.cancelRepeat && axiosTool.throwHttpError(err);
            return Promise.reject(err.response.data || {});
        }
        return Promise.reject({});
    },
};
// desc 对外抛出函数，用以创建axios实例
// @params baseURL:string required
const createHttpInstance = (changeConfig, request = true, response = true) => {
    const customAxios = axios.create(Object.assign({}, axiosConfig, Object.assign({}, changeConfig)));
    request && customAxios.interceptors.request.use(requestInterceptors.success, requestInterceptors.error);
    response && customAxios.interceptors.response.use(responseInterceptors.success, responseInterceptors.error);
    return customAxios;
};
export default createHttpInstance;
